/**
 * Google Analytics 4 (GA4) Integration Service
 * 
 * Configurazione per Gildy:
 * - Nome stream: gildy
 * - URL dello stream: https://www.gildy.it/
 * - ID stream: 10474205823
 * - ID misurazione: G-6S5TPK9ECB
 */

// Google Analytics configuration
export const GA_MEASUREMENT_ID = 'G-6S5TPK9ECB';
export const GA_STREAM_ID = '10474205823';

// Declare gtag function for TypeScript
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

/**
 * Initialize Google Analytics
 * This should be called on both client and server side
 */
export const initializeGA = (): void => {
  // Only initialize on client side
  if (typeof window === 'undefined') return;

  // Initialize dataLayer if it doesn't exist
  window.dataLayer = window.dataLayer || [];
  
  // Define gtag function
  window.gtag = function gtag(...args: any[]) {
    window.dataLayer.push(args);
  };

  // Configure GA
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
    page_title: document.title,
    page_location: window.location.href,
    send_page_view: true,
    // Enhanced ecommerce and user engagement
    enhanced_measurement: true,
    // Cookie settings for GDPR compliance
    anonymize_ip: true,
    cookie_flags: 'SameSite=None;Secure',
    // Custom parameters for Gildy
    custom_map: {
      'custom_parameter_1': 'metal_type',
      'custom_parameter_2': 'dealer_location',
      'custom_parameter_3': 'price_alert_type'
    }
  });

  console.log('[GA] Google Analytics initialized with ID:', GA_MEASUREMENT_ID);
};

/**
 * Track page views
 */
export const trackPageView = (url: string, title?: string): void => {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('config', GA_MEASUREMENT_ID, {
    page_path: url,
    page_title: title || document.title,
    page_location: window.location.origin + url
  });

  console.log('[GA] Page view tracked:', url);
};

/**
 * Track custom events
 */
export const trackEvent = (
  eventName: string,
  parameters: {
    event_category?: string;
    event_label?: string;
    value?: number;
    [key: string]: any;
  } = {}
): void => {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('event', eventName, {
    event_category: parameters.event_category || 'engagement',
    event_label: parameters.event_label,
    value: parameters.value,
    ...parameters
  });

  console.log('[GA] Event tracked:', eventName, parameters);
};

/**
 * Track metal price interactions
 */
export const trackMetalPriceEvent = (
  action: 'view_price' | 'price_alert_set' | 'price_chart_view',
  metalType: 'gold' | 'silver',
  additionalData: Record<string, any> = {}
): void => {
  trackEvent(action, {
    event_category: 'metal_prices',
    event_label: metalType,
    metal_type: metalType,
    ...additionalData
  });
};

/**
 * Track dealer interactions
 */
export const trackDealerEvent = (
  action: 'view_dealer' | 'contact_dealer' | 'favorite_dealer' | 'rate_dealer',
  dealerId: string,
  dealerLocation?: string,
  additionalData: Record<string, any> = {}
): void => {
  trackEvent(action, {
    event_category: 'dealers',
    event_label: dealerId,
    dealer_id: dealerId,
    dealer_location: dealerLocation,
    ...additionalData
  });
};

/**
 * Track blog interactions
 */
export const trackBlogEvent = (
  action: 'view_post' | 'share_post' | 'search_blog',
  postId?: string,
  category?: string,
  additionalData: Record<string, any> = {}
): void => {
  trackEvent(action, {
    event_category: 'blog',
    event_label: postId || category,
    post_id: postId,
    blog_category: category,
    ...additionalData
  });
};

/**
 * Track user authentication events
 */
export const trackAuthEvent = (
  action: 'login' | 'register' | 'logout' | 'password_reset',
  method?: string,
  additionalData: Record<string, any> = {}
): void => {
  trackEvent(action, {
    event_category: 'authentication',
    method: method,
    ...additionalData
  });
};

/**
 * Track search events
 */
export const trackSearchEvent = (
  searchTerm: string,
  searchType: 'dealers' | 'blog' | 'general',
  resultsCount?: number
): void => {
  trackEvent('search', {
    event_category: 'search',
    search_term: searchTerm,
    search_type: searchType,
    results_count: resultsCount
  });
};

/**
 * Track conversion events (for business goals)
 */
export const trackConversionEvent = (
  conversionType: 'contact_form' | 'dealer_activation_request' | 'newsletter_signup',
  value?: number,
  additionalData: Record<string, any> = {}
): void => {
  trackEvent('conversion', {
    event_category: 'conversions',
    conversion_type: conversionType,
    value: value,
    currency: 'EUR',
    ...additionalData
  });
};

/**
 * Set user properties
 */
export const setUserProperties = (properties: Record<string, any>): void => {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('config', GA_MEASUREMENT_ID, {
    user_properties: properties
  });

  console.log('[GA] User properties set:', properties);
};

/**
 * Get the Google Analytics script URL
 */
export const getGAScriptUrl = (): string => {
  return `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
};

/**
 * Generate the Google Analytics script tag for SSR
 */
export const generateGAScriptTag = (): string => {
  return `
    <!-- Google Analytics -->
    <script async src="${getGAScriptUrl()}"></script>
    <script>
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${GA_MEASUREMENT_ID}', {
        page_title: document.title,
        page_location: window.location.href,
        send_page_view: true,
        enhanced_measurement: true,
        anonymize_ip: true,
        cookie_flags: 'SameSite=None;Secure',
        custom_map: {
          'custom_parameter_1': 'metal_type',
          'custom_parameter_2': 'dealer_location',
          'custom_parameter_3': 'price_alert_type'
        }
      });
    </script>
  `;
};

/**
 * Enhanced ecommerce tracking for dealer services
 */
export const trackDealerServiceView = (
  dealerId: string,
  dealerName: string,
  location: string,
  serviceType: string = 'gold_buying'
): void => {
  trackEvent('view_item', {
    event_category: 'ecommerce',
    currency: 'EUR',
    value: 0, // No monetary value for viewing
    items: [{
      item_id: dealerId,
      item_name: dealerName,
      item_category: 'dealer_service',
      item_category2: serviceType,
      item_variant: location,
      quantity: 1
    }]
  });
};

/**
 * Track performance metrics
 */
export const trackPerformanceMetrics = (): void => {
  if (typeof window === 'undefined' || !window.performance) return;

  // Wait for page load to complete
  window.addEventListener('load', () => {
    setTimeout(() => {
      const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
      
      if (navigation) {
        // Track page load time
        const loadTime = navigation.loadEventEnd - navigation.fetchStart;
        
        trackEvent('page_load_time', {
          event_category: 'performance',
          value: Math.round(loadTime),
          metric_name: 'load_time_ms'
        });

        // Track Time to First Byte (TTFB)
        const ttfb = navigation.responseStart - navigation.fetchStart;
        
        trackEvent('ttfb', {
          event_category: 'performance',
          value: Math.round(ttfb),
          metric_name: 'ttfb_ms'
        });
      }
    }, 1000);
  });
};
